import { AccountRow } from "@/components/accountSwitcher/AccountRow.tsx";
import {
  getProviderLabel,
  useAccountActions,
} from "@/components/accountSwitcher/useAccountActions.ts";
import { GoogleIcon } from "@/components/icons/GoogleIcon.tsx";
import { OutlookIcon } from "@/components/icons/OutlookIcon.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { LogoutOtherDevices } from "./LogoutOtherDevices.tsx";

/**
 * The other-accounts block of Settings → Account: every account except the
 * current one (which the profile card above already shows), plus the two
 * add-account entry points and the session controls. Clicking a row switches
 * and lands back on this tab, so the switched-to account surfaces in the
 * profile card up top. Logout renders here (right-aligned in the add-account
 * row, beside the log-out-other-devices control) but the session it ends
 * belongs to the whole Settings page, so the handler comes from the parent.
 */
export const OtherAccounts = ({ onLogout }: { onLogout: () => void }) => {
  const { userContext } = useCurrentUserContext();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { switchMutation, startAddAccount } = useAccountActions({
    postSwitchPath: "/settings/account",
  });

  if (!userContext) return null;

  const accounts = userContext.user.accounts ?? [];
  const otherAccounts = accounts.filter((a) => a.id !== userContext.user.id);

  return (
    <div className="mt-3 space-y-3">
      {otherAccounts.map((acct) => {
        const primary = acct.displayName ?? getProviderLabel(acct.provider);
        return (
          // Each switchable account gets the same card treatment as the active
          // account above, so the section reads as one list of peer accounts —
          // the active one is simply the card with the detail sections.
          <div key={acct.id} className="rounded-2xl border bg-card p-2">
            <AccountRow
              primary={primary}
              avatarName={acct.displayName ?? primary}
              picture={acct.picture ?? undefined}
              secondary={getProviderLabel(acct.provider)}
              isCurrent={false}
              disabled={switchMutation.isPending}
              statsigName="settings-account-switch"
              onClick={() =>
                switchMutation.mutate({ accountId: acct.id, displayName: acct.displayName })
              }
              action={
                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                  Switch to this account
                </span>
              }
            />
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => startAddAccount("google")}
          data-statsig-name="settings-account-add-google"
          className="gap-1.5!"
        >
          {/* The provider icons carry their own role="img" + aria-label; hide them from
              the a11y tree so the button's accessible name is just its text label. */}
          <span aria-hidden="true" className="shrink-0">
            <GoogleIcon className="size-4" />
          </span>
          Add Google account
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => startAddAccount("microsoft")}
          data-statsig-name="settings-account-add-outlook"
          className="gap-1.5!"
        >
          <span aria-hidden="true" className="shrink-0">
            <OutlookIcon className="size-4" />
          </span>
          Add Outlook account
        </Button>
        {/* Desktop: pushed to the row's right edge. Below md: basis-full forces the
            wrapper onto its own wrapped row, where the buttons sit at the far left. */}
        <div className="ml-auto flex flex-wrap gap-2 max-md:ml-0 max-md:basis-full">
          <LogoutOtherDevices />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLogoutDialogOpen(true)}
            data-statsig-name="settings-account-logout"
            className="gap-1.5! shrink-0"
          >
            <LogOut className="size-4" strokeWidth={2.5} />
            Log out
          </Button>
        </div>
      </div>
      {/* Logout ends the session for every account on this device, so it gets the
          same interstitial-confirm treatment as other consequential actions
          (see DeleteAccount) instead of an always-visible warning. */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Log out?</DialogTitle>
            <DialogDescription>
              Logging out signs you out of all accounts on this device. Do you want to log out?
            </DialogDescription>
          </DialogHeader>
          {/* Override the footer's stacked mobile default: two short actions
              fit side by side at every width. */}
          <DialogFooter className="flex-row justify-end">
            <Button
              variant="outline"
              onClick={() => setLogoutDialogOpen(false)}
              data-statsig-name="settings-account-logout-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={onLogout}
              data-statsig-name="settings-account-logout-confirm"
            >
              Log out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
